import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import useApiCall from "./useApiCall";
import { saveApps, selectApp } from "../slices/AppsSlice";

/**
 * Fetches the active apps list (if not already loaded) and resolves the
 * current app from the URL's leading path segment into Redux.
 *
 * This used to live only in Navbar, so routes that render without Navbar
 * (e.g. the full-screen chat and embedded-view routes) could be hit directly
 * — a hard refresh or a shared link — with the apps list never fetched and
 * `selectedApp` never resolved. Call this once from NavigationRoutes, before
 * any route-tree branching, so every route gets it regardless of chrome.
 */
export default function useEnsureAppsLoaded() {
  const dispatch = useDispatch();
  const location = useLocation();
  const tokens = useSelector((state) => state?.tokens.value);
  const apps = useSelector((state) => state?.apps.list || []);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();

  const appNameFromUrl = location.pathname.split("/").filter(Boolean)[0];

  useEffect(() => {
    if (!tokens?.user_token || apps.length > 0) return;

    let cancelled = false;
    makeRequest("/v1/apps?status=active", {
      method: "GET",
      authTokens: tokens,
      ignore_error: true,
    })
      .then((data) => {
        if (!cancelled && Array.isArray(data) && data.length) {
          dispatch(saveApps(data));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens?.user_token, apps.length]);

  useEffect(() => {
    if (!appNameFromUrl || currentApp?.url_code === appNameFromUrl) return;
    const match = apps.find((app) => app.url_code === appNameFromUrl);
    if (match) dispatch(selectApp(match));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appNameFromUrl, apps, currentApp?.url_code]);
}
